import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { ArrowDownRight, ArrowUpRight, RefreshCw, Store, FileText, PackageX, Wallet, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { useMobile } from "./MobileApp";
import { AlertCard, Glass, ProLock, Rise, SectionTitle, Spinner, clp, hora, saludo } from "./ui";

const Delta = ({ v, testId }) => v == null ? <span data-testid={testId} className="text-slate-500 text-xs">sin ventas ayer</span> : (
  <span data-testid={testId} className={`inline-flex items-center gap-0.5 font-mono text-xs font-semibold ${v >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
    {v >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{v > 0 ? "+" : ""}{v}%
  </span>
);

const Hero = ({ d, onRefresh, busy }) => (
  <Glass className="relative overflow-hidden p-5 border-cyan-500/30 shadow-[0_0_40px_rgba(6,182,212,0.12)]" data-testid="kpi-ventas-hoy">
    <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-cyan-500/15 blur-3xl" />
    <div className="relative flex items-center justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-400/90">Venta del día</p>
      <button onClick={onRefresh} data-testid="mobile-refresh" className="text-slate-500 hover:text-cyan-300 transition-colors duration-200">
        <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
      </button>
    </div>
    <p className="relative mt-2 text-[40px] leading-none font-extrabold tracking-tight font-mono tabular-nums" data-testid="kpi-ventas-hoy-total">{clp(d.total)}</p>
    <div className="relative mt-4 grid grid-cols-3 gap-3 text-xs">
      <div><p className="text-slate-500">Tickets</p><p className="font-mono text-slate-100 font-semibold text-base" data-testid="kpi-tickets">{d.tickets}</p></div>
      <div><p className="text-slate-500">Promedio</p><p className="font-mono text-slate-100 font-semibold text-base" data-testid="kpi-promedio">{clp(d.promedio)}</p></div>
      <div><p className="text-slate-500">vs ayer</p><div className="mt-0.5"><Delta v={d.vs_ayer} testId="kpi-vs-ayer" /></div></div>
    </div>
    <p className="relative mt-3 text-[10px] text-slate-500 font-mono">Actualizado {hora(d.actualizado)} · ayer a esta hora {clp(d.total_ayer)}</p>
  </Glass>
);

const Hourly = ({ data }) => (
  <Glass className="p-4 pt-3" data-testid="home-hourly-chart">
    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-2">Ventas por hora</p>
    <div className="h-28">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data.filter((x) => x.hora >= 7 || x.total > 0)}>
          <defs><linearGradient id="hg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22D3EE" stopOpacity={0.5} /><stop offset="100%" stopColor="#22D3EE" stopOpacity={0} /></linearGradient></defs>
          <XAxis dataKey="hora" tick={{ fill: "#475569", fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0B1324", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, fontSize: 12 }} formatter={(v) => clp(v)} labelFormatter={(h) => `${h}:00`} />
          <Area dataKey="total" stroke="#22D3EE" strokeWidth={2} fill="url(#hg)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </Glass>
);

const Stat = ({ icon: I, label, value, tone = "text-slate-100", testId, onClick }) => (
  <button onClick={onClick} data-testid={testId} className="text-left rounded-2xl border border-white/[0.08] bg-[#0B1324] p-3.5 hover:bg-[#0F1A30] transition-colors duration-200">
    <I className="w-4 h-4 text-slate-500" />
    <p className={`mt-2 font-mono text-xl font-bold ${tone}`}>{value}</p>
    <p className="text-[11px] text-slate-400">{label}</p>
  </button>
);

const Folios = ({ folios }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="home-folios">
    {folios.map((f) => (
      <Glass key={f.tipo_dte} className={`p-3 ${f.estado !== "OK" ? "border-amber-500/40" : ""}`} data-testid={`folio-${f.tipo_dte}`}>
        <p className="text-xs font-semibold text-slate-200 leading-tight" data-testid={`folio-nombre-${f.tipo_dte}`}>{f.nombre || `Documento ${f.tipo_dte}`}</p>
        <p className={`mt-1 font-mono text-xl font-bold ${f.estado === "AGOTADO" ? "text-rose-400" : f.estado === "BAJO" ? "text-amber-400" : "text-slate-100"}`}>{f.disponibles.toLocaleString("es-CL")}</p>
        <p className="text-[10px] text-slate-500">disponibles · aviso bajo {f.minimo} · <span className="font-mono">cód. {f.tipo_dte}</span></p>
      </Glass>
    ))}
  </div>
);

export default function Home() {
  const { me, storeId } = useMobile();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setD((await api.get("/mobile/dashboard", { params: { store_id: storeId || undefined } })).data); }
    finally { setBusy(false); }
  }, [storeId]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!d) return <Spinner />;
  const nombre = (me.user.name || "").split(" ")[0];
  return (
    <div className="pt-5" data-testid="mobile-home">
      <Rise><h1 className="text-2xl font-bold font-display tracking-tight">{saludo()}{nombre ? `, ${nombre}` : ""}</h1>
        <p className="text-xs text-slate-400 mt-1">{d.cajas_abiertas} caja(s) abierta(s) · {d.por_sucursal.filter((s) => s.tickets > 0).length} sucursal(es) vendiendo hoy</p></Rise>
      <Rise i={1} className="mt-5"><Hero d={d} onRefresh={load} busy={busy} /></Rise>
      {d.por_hora && <Rise i={2} className="mt-3"><Hourly data={d.por_hora} /></Rise>}
      <Rise i={3} className="mt-3 grid grid-cols-3 gap-2">
        <Stat icon={Wallet} label="Cajas abiertas" value={d.cajas_abiertas} testId="stat-cajas-abiertas" onClick={() => nav("/movil/cajas")} />
        <Stat icon={Scale} label="Descuadres hoy" value={d.descuadres_hoy} tone={d.descuadres_hoy ? "text-rose-400" : "text-emerald-400"} testId="stat-descuadres" onClick={() => nav("/movil/cajas")} />
        <Stat icon={PackageX} label="Stock crítico" value={d.stock_critico_total} tone={d.stock_critico_total ? "text-amber-400" : "text-slate-100"} testId="stat-stock-critico" onClick={() => nav("/movil/alertas?tipo=STOCK_CRITICO")} />
      </Rise>

      {me.stores.length > 1 && !storeId && (<>
        <SectionTitle>Por sucursal</SectionTitle>
        <div className="space-y-2" data-testid="home-por-sucursal">
          {d.por_sucursal.map((s, i) => (
            <Rise key={s.store_id} i={i}>
              <Glass className="p-4 flex items-center gap-3" data-testid={`home-store-${s.store_id}`}>
                <div className="w-9 h-9 rounded-xl grid place-items-center bg-cyan-500/10 border border-cyan-500/20 text-cyan-300"><Store className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{s.nombre}</p>
                  <p className="text-[11px] text-slate-500">{s.tickets} tickets · {s.cajas_abiertas} caja(s) abierta(s)</p></div>
                <p className="font-mono font-bold">{clp(s.total)}</p>
              </Glass>
            </Rise>
          ))}
        </div>
      </>)}

      {d.folios && (<><SectionTitle right={<FileText className="w-4 h-4 text-slate-500" />}>Folios DTE disponibles</SectionTitle><Folios folios={d.folios} /></>)}

      {d.stock_critico.length > 0 && (<>
        <SectionTitle>Stock crítico</SectionTitle>
        <div className="space-y-2" data-testid="home-stock-critico">
          {d.stock_critico.map((p) => (
            <Glass key={`${p.store_id}-${p.product_id}`} className="p-3.5 flex items-center justify-between border-amber-500/30">
              <div className="min-w-0"><p className="text-sm font-semibold truncate">{p.nombre}</p><p className="text-[11px] text-slate-500 truncate">{p.store_nombre} · mín. {p.minimo}</p></div>
              <p className={`font-mono font-bold ${p.severidad === "AGOTADO" ? "text-rose-400" : "text-amber-400"}`}>{Number(p.stock).toLocaleString("es-CL")} uds</p>
            </Glass>
          ))}
        </div>
      </>)}

      <SectionTitle right={<button onClick={() => nav("/movil/alertas")} className="text-[11px] text-cyan-300 font-semibold" data-testid="home-ver-alertas">Ver todas</button>}>Últimas alertas</SectionTitle>
      <div className="space-y-2">
        {d.ultimas_alertas.length ? d.ultimas_alertas.map((a, i) => <AlertCard key={a.alert_id} a={a} i={i} onClick={() => nav("/movil/alertas")} />)
          : <p className="text-xs text-slate-500 px-1">Aún no hay alertas. Aparecerán aquí al abrir o cerrar cajas, anular ventas y más.</p>}
      </div>

      {me.tier !== "PRO" && (<>
        <SectionTitle>Más con Ai-POS Pro</SectionTitle>
        <ProLock testId="home-pro-lock" titulo="Ventas por hora, ranking y utilidad" detalle="Gráficos en vivo, top productos, margen, medios de pago, vendedores e insights de IA sobre tu negocio." />
      </>)}
    </div>
  );
}
